// The live map: MapLibre on OpenFreeMap's vector tiles (free, no key) until
// the Protomaps file on Cloudflare R2 replaces them (stack plan, phase 2).
// As the approved design draws it (Ruben, 2 Nov 2026: « build the map »):
// every home with a position as a dot in its gas colour, the open orders'
// homes as pins, riders as dark discs with a dashed line to the door they are
// heading to, stations as squares, the pilot zones as soft blue areas. Every
// point shows a card on hover; a click acts (open the home or the order, pick
// the rider). Layer switches turn each kind on or off, kept per computer.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import maplibregl, { type GeoJSONSource, type Map as MlMap, type Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Live, LiveRider, MapHome, OpsOrder, StationStock } from '../lib/types';
import { activityPoints, zoneShapes } from '../lib/zones';
import { LANG, S } from '../lib/strings';
import { minutesBetween, rel } from '../lib/format';
import { displayName } from '../lib/brands';
import { useTip } from './Tip';

const STYLE = 'https://tiles.openfreemap.org/styles/positron';
const YAOUNDE: [number, number] = [11.5167, 3.8667];
// The design's --m-zone and --m-zone-line; the dark theme inverts the canvas, blue stays blue.
const ZONE_FILL = 'rgba(14,116,188,0.09)';
const ZONE_LINE = 'rgba(14,116,188,0.6)';
// Gas colours, as the household app draws a bottle: blue, amber under 30 %, red under 20 %, grey without a reading.
const GAS = { ok: '#0E74BC', mid: '#F5A623', low: '#FF5757', none: '#9AA4B2' };
const MOTO = 'M6 17a3 3 0 1 0 0-.01M18 17a3 3 0 1 0 0-.01M6 17l3-6h4l2 3h3M9 11 8 8H6';

export type Layer = 'riders' | 'orders' | 'homes' | 'stations' | 'zones';
const LAYERS: Layer[] = ['riders', 'orders', 'homes', 'stations', 'zones'];
const LKEY = 'mygaz-ops-map-layers';
const readLayers = (): Record<Layer, boolean> => {
  const all = { riders: true, orders: true, homes: true, stations: true, zones: true };
  try { return { ...all, ...(JSON.parse(localStorage.getItem(LKEY) ?? '{}') as Partial<Record<Layer, boolean>>) }; } catch { return all; }
};

const gas = (h: MapHome) => {
  if (!h.connected || !h.home || h.home.level == null || !h.home.online) return 'none';
  return h.home.level < 20 ? 'low' : h.home.level < 30 ? 'mid' : 'ok';
};
const daysTxt = (d: number | undefined) => (d == null ? '' : S.map.days(Math.max(0, Math.floor(d))));

function homeCard(h: MapHome): ReactNode {
  const g = gas(h);
  const brand = h.marketer ? displayName(h.marketer.code, h.marketer.name) : null;
  const level = !h.connected || !h.home ? S.map.noKit
    : !h.home.online ? S.map.silent(h.home.received_at ? rel(minutesBetween(h.home.received_at), LANG) : '')
    : `${h.home.level} %${h.pace ? ' · ' + daysTxt(h.pace.days_left) : ''}`;
  return (
    <>
      <b>{h.name}</b>
      <div className="tr"><span>{h.quartier}{brand ? ` · ${brand}` : ''}</span>{h.size_kg} kg</div>
      <div className="tr"><span>{S.map.gas}</span><em className={'tlv ' + g}>{level}</em></div>
      <div className="no">{h.open_order ? S.map.orderOn : h.failed ? S.map.failed : S.map.noOrder} · {S.map.openHome}</div>
    </>
  );
}
function riderCard(r: LiveRider, eta: string | null | undefined): ReactNode {
  const st = r.order ? S.lv.status.busy : r.live ? S.lv.status.free : S.lv.status.stale;
  const mins = eta ? Math.max(0, Math.round((new Date(eta).getTime() - Date.now()) / 60_000)) : null;
  return (
    <>
      <b>{r.name}</b>
      <div className="tr"><span>{r.agency}</span>{st}</div>
      {r.order && <div className="tr"><span>{r.order.ref}</span>{r.order.customer}, {r.order.quartier}</div>}
      {r.order && mins != null && <div className="tr"><span>{S.map.arrival}</span>{S.map.inMin(mins)}</div>}
      {r.at && <div className="no">{S.lv.seen(rel(minutesBetween(r.at), LANG))}</div>}
    </>
  );
}
function orderCard(h: Live['homes'][number]): ReactNode {
  return (
    <>
      <b>{h.customer}</b>
      <div className="tr"><span>{h.quartier}</span>{S.st[h.status]}</div>
      <div className="no">{S.map.openOrder}</div>
    </>
  );
}
function stationCard(name: string, stock: StationStock[]): ReactNode {
  return (
    <>
      <b>{name}</b>
      {stock.length ? stock.map((x) => <div className="tr" key={x.code + x.size_kg}><span><i className="tdot" style={{ background: x.brand_color }} />{displayName(x.code, x.name)}</span>{S.map.full(x.full)}</div>)
        : <div className="no">{S.map.noStock}</div>}
    </>
  );
}

function riderEl(name: string, busy: boolean, live: boolean) {
  const el = document.createElement('button');
  el.className = 'lm-rider' + (busy ? ' busy' : '') + (live ? '' : ' stale');
  el.setAttribute('aria-label', name);
  el.innerHTML = `<svg class="i xs" viewBox="0 0 24 24" aria-hidden="true"><path d="${MOTO}"/></svg>`;
  return el;
}
function homeEl(label: string, placed: boolean) {
  const el = document.createElement('button');
  el.className = 'lm-home' + (placed ? ' placed' : '');
  el.setAttribute('aria-label', label);
  return el;
}
function stationEl(label: string) {
  const el = document.createElement('button');
  el.className = 'lm-station';
  el.setAttribute('aria-label', label);
  return el;
}

interface Props {
  live: Live | null; padding?: number; controls?: boolean;
  onRider?: (id: string) => void; onHome?: (id: string) => void; onOrder?: (id: string) => void;
  /** Open orders, for a busy rider's time to the door. */
  orders?: OpsOrder[] | null;
  /** Room taken on the left by a panel over the map (the Live view's rider list). */
  inset?: number;
}

export function LiveMap({ live, onRider, onHome, onOrder, orders, padding = 56, controls = true, inset = 0 }: Props) {
  const tip = useTip();
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const marks = useRef<Marker[]>([]);
  const framed = useRef(false);
  const ready = useRef(false);
  const [noGl, setNoGl] = useState(false);
  const [layers, setLayers] = useState(readLayers);
  const [loaded, setLoaded] = useState(false);
  const homes = useRef(new Map<string, MapHome>());
  const cb = useRef({ onHome, tip });
  useEffect(() => { cb.current = { onHome, tip }; }, [onHome, tip]);
  const etas = useMemo(() => new Map((orders ?? []).map((o) => [o.id, o.eta_at])), [orders]);

  // Everything the GL layers draw, as GeoJSON.
  const geo = useMemo(() => {
    const hs = live?.map?.homes ?? [];
    const dots = { type: 'FeatureCollection' as const, features: hs.map((h) => ({ type: 'Feature' as const, properties: { id: h.customer_id, c: GAS[gas(h)] }, geometry: { type: 'Point' as const, coordinates: [h.lng, h.lat] } })) };
    const dest = new Map((live?.homes ?? []).map((h) => [h.order_id, [h.lng, h.lat]]));
    const trips = { type: 'FeatureCollection' as const, features: (live?.riders ?? []).flatMap((r) => {
      const to = r.order ? dest.get(r.order.id) : undefined;
      return to ? [{ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates: [[r.lng, r.lat], to] } }] : [];
    }) };
    return { dots, trips, zones: zoneShapes(live?.zones ?? []) };
  }, [live]);

  // Data into the layers and the markers; kept in a ref so the map's load handler calls the latest.
  const draw = useRef<() => void>(() => {});
  useEffect(() => {
    draw.current = () => {
      const m = map.current;
      if (!m || !ready.current || !live) return;
      (m.getSource('zones') as GeoJSONSource | undefined)?.setData(geo.zones);
      (m.getSource('trips') as GeoJSONSource | undefined)?.setData(geo.trips);
      (m.getSource('homes') as GeoJSONSource | undefined)?.setData(geo.dots);
      homes.current = new Map((live.map?.homes ?? []).map((h) => [h.customer_id, h]));
      marks.current.forEach((k) => k.remove());
      marks.current = [];
      const pts: [number, number][] = [];
      const add = (el: HTMLElement, lng: number, lat: number, card: () => ReactNode, click?: () => void) => {
        el.addEventListener('mouseenter', (e) => tip.show(card(), e.clientX, e.clientY));
        el.addEventListener('mousemove', (e) => tip.show(card(), e.clientX, e.clientY));
        el.addEventListener('mouseleave', () => tip.hide());
        el.addEventListener('focus', () => { const r = el.getBoundingClientRect(); tip.show(card(), r.right, r.top); });
        el.addEventListener('blur', () => tip.hide());
        if (click) el.addEventListener('click', () => { tip.hide(); click(); });
        marks.current.push(new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(m));
        pts.push([lng, lat]);
      };
      const stock = live.map?.stock ?? [];
      live.stations.forEach((s) => add(stationEl(s.name), s.lng, s.lat, () => stationCard(s.name, stock.filter((x) => x.station_id === s.id))));
      live.homes.forEach((h) => add(homeEl(`${h.customer} · ${h.quartier}`, h.status === 'placed'), h.lng, h.lat, () => orderCard(h), onOrder ? () => onOrder(h.order_id) : undefined));
      live.riders.forEach((r) => add(riderEl(r.name, !!r.order, r.live), r.lng, r.lat, () => riderCard(r, r.order ? etas.get(r.order.id) : null), onRider ? () => onRider(r.id) : undefined));
      (live.map?.homes ?? []).forEach((h) => pts.push([h.lng, h.lat]));
      geo.zones.features.forEach((f) => f.geometry.coordinates[0].forEach(([lng, lat]) => pts.push([lng, lat])));
      // Open on where the activity is: a home saved far away (a simulator's default spot) must not zoom the city out.
      const keep = activityPoints(pts);
      if (!framed.current && keep.length > 1) {
        const b = keep.reduce((bb, p) => bb.extend(p), new maplibregl.LngLatBounds(keep[0], keep[0]));
        m.fitBounds(b, { padding: { top: padding, bottom: padding, right: padding, left: padding + inset }, maxZoom: 14.5, duration: 0 });
        framed.current = true;
      }
    };
    draw.current();
  }, [live, geo, etas, onOrder, onRider, tip, padding, inset]);

  useEffect(() => {
    if (!box.current) return;
    let m: MlMap;
    try {
      m = new maplibregl.Map({ container: box.current, style: STYLE, center: YAOUNDE, zoom: 12.2, attributionControl: false, dragRotate: false, pitchWithRotate: false });
    } catch {
      setNoGl(true); // no WebGL on this computer: say so instead of taking the console down
      return;
    }
    m.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
    m.on('load', () => {
      // Under the street names, above the land and roads.
      const firstSymbol = m.getStyle().layers.find((l) => l.type === 'symbol')?.id;
      const empty = { type: 'FeatureCollection' as const, features: [] };
      m.addSource('zones', { type: 'geojson', data: empty });
      m.addLayer({ id: 'zone-fill', type: 'fill', source: 'zones', paint: { 'fill-color': ZONE_FILL } }, firstSymbol);
      m.addLayer({ id: 'zone-line', type: 'line', source: 'zones', paint: { 'line-color': ZONE_LINE, 'line-width': 1.6, 'line-dasharray': [3, 2.5] } }, firstSymbol);
      // The quartier's name inside its zone (the base map does not label quartiers at this zoom).
      m.addLayer({ id: 'zone-name', type: 'symbol', source: 'zones', layout: { 'text-field': ['get', 'name'], 'text-font': ['Noto Sans Bold'], 'text-size': 11, 'text-letter-spacing': 0.08, 'text-transform': 'uppercase' }, paint: { 'text-color': '#0B5C96', 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 1.4 } });
      m.addSource('trips', { type: 'geojson', data: empty });
      m.addLayer({ id: 'trip-line', type: 'line', source: 'trips', layout: { 'line-cap': 'round' }, paint: { 'line-color': '#0E74BC', 'line-width': 3, 'line-opacity': 0.75, 'line-dasharray': [0.5, 2] } });
      m.addSource('homes', { type: 'geojson', data: empty });
      m.addLayer({ id: 'home-dot', type: 'circle', source: 'homes', paint: {
        'circle-color': ['get', 'c'], 'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3.5, 13, 6, 16, 9],
        'circle-stroke-color': '#FFFFFF', 'circle-stroke-width': 2 } });
      m.on('mousemove', 'home-dot', (e) => {
        const h = homes.current.get(String(e.features?.[0]?.properties?.id));
        if (!h) return;
        m.getCanvas().style.cursor = 'pointer';
        cb.current.tip.show(homeCard(h), e.originalEvent.clientX, e.originalEvent.clientY);
      });
      m.on('mouseleave', 'home-dot', () => { m.getCanvas().style.cursor = ''; cb.current.tip.hide(); });
      m.on('click', 'home-dot', (e) => { const id = e.features?.[0]?.properties?.id; if (id) { cb.current.tip.hide(); cb.current.onHome?.(String(id)); } });
      ready.current = true;
      setLoaded(true);
      draw.current();
    });
    map.current = m;
    // A tab kept in the background is display:none; the map redraws at full size when it shows again.
    const ro = new ResizeObserver(() => { if (box.current?.clientWidth) m.resize(); });
    ro.observe(box.current);
    return () => { ro.disconnect(); m.remove(); map.current = null; ready.current = false; };
  }, []);

  // Layer switches: GL layers by visibility, markers by a class on the box.
  useEffect(() => {
    const m = map.current;
    if (!m || !loaded) return;
    const vis = (on: boolean) => (on ? 'visible' : 'none');
    m.setLayoutProperty('home-dot', 'visibility', vis(layers.homes));
    m.setLayoutProperty('trip-line', 'visibility', vis(layers.riders));
    ['zone-fill', 'zone-line', 'zone-name'].forEach((id) => m.setLayoutProperty(id, 'visibility', vis(layers.zones)));
  }, [layers, loaded]);
  useEffect(() => { try { localStorage.setItem(LKEY, JSON.stringify(layers)); } catch { /* storage off */ } }, [layers]);

  const counts: Record<Layer, number> = {
    riders: live?.riders.length ?? 0, orders: live?.homes.length ?? 0, homes: live?.map?.homes.length ?? 0,
    stations: live?.stations.length ?? 0, zones: live?.zones?.length ?? 0,
  };
  const off = LAYERS.filter((l) => !layers[l]).map((l) => 'no-' + l).join(' ');

  return (
    <div className={'map lmap ' + off}>
      <div ref={box} className="lmap-gl" />
      {noGl && <div className="lm-nogl">{S.noMap}</div>}
      {controls && !noGl && (
        <div className="mlayers glass" role="group" aria-label={S.map.layers} style={{ left: 12 + inset }}>
          {LAYERS.map((l) => (
            <button key={l} type="button" aria-pressed={layers[l]} onClick={() => setLayers((x) => ({ ...x, [l]: !x[l] }))}>
              <span className={'d ' + l} />{S.map.layer[l]}<em className="num">{counts[l]}</em>
            </button>
          ))}
        </div>
      )}
      {controls && !noGl && layers.homes && counts.homes > 0 && (
        <div className="mlegend glass" style={{ left: 12 + inset }}>
          {(['ok', 'mid', 'low', 'none'] as const).map((k) => <span key={k}><i style={{ background: GAS[k] }} />{S.map.legend[k]}</span>)}
        </div>
      )}
      {controls && <div className="mctl">
        <button aria-label="Zoom +" onClick={() => map.current?.zoomIn()}><svg className="i s" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg></button>
        <button aria-label="Zoom −" onClick={() => map.current?.zoomOut()}><svg className="i s" viewBox="0 0 24 24"><path d="M5 12h14" /></svg></button>
      </div>}
    </div>
  );
}
